import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { Customer, TimePurchase, CustomerTransaction, Assignment, TimeEntry } from '../types';
import { retryWithBackoff, setupVisibilityListener, setupOnlineListener } from '../utils/sessionManager';

export const useCustomerData = (userId: string | null) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [purchases, setPurchases] = useState<TimePurchase[]>([]);
  const [transactions, setTransactions] = useState<CustomerTransaction[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMissingTablesError, setHasMissingTablesError] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const lastLoadTime = useRef(0);
  const LOAD_DEBOUNCE_MS = 1000;

  const loadCustomers = useCallback(async () => {
    try {
      const { data: customersData, error } = await supabase
        .from('customers')
        .select('*');

      if (error) throw error;

      if (customersData) {
        let mappedCustomers: Customer[] = customersData.map((row: any) => ({
          id: row.id,
          userId: row.user_id,
          parentCustomerId: row.parent_customer_id || null,
          name: row.name,
          email: row.email,
          phone: row.phone,
          organizationNumber: row.organization_number,
          address: row.address,
          currentBalanceMinutes: Number(row.current_balance_minutes) || 0,
          totalPurchasedMinutes: Number(row.total_purchased_minutes) || 0,
          totalUsedMinutes: Number(row.total_used_minutes) || 0,
          currentBalanceKm: Number(row.current_balance_km) || 0,
          totalPurchasedKm: Number(row.total_purchased_km) || 0,
          totalUsedKm: Number(row.total_used_km) || 0,
          status: row.status,
          notes: row.notes,
          billingType: row.billing_type || 'time_package',
          kmRate: Number(row.km_rate) || 5.5,
          perJobHourlyRate: Number(row.per_job_hourly_rate) || 595,
          perJobMinBillingHours: Number(row.per_job_min_billing_hours) || 1.0,
          perJobIncrementMinutes: Number(row.per_job_increment_minutes) || 30,
          perJobKmRate: Number(row.per_job_km_rate) || 5.5,
          perJobMinKmCharge: Number(row.per_job_min_km_charge) || 350,
          perJobFixedPrice: row.per_job_fixed_price ? Number(row.per_job_fixed_price) : undefined,
          usesDistancePricing: row.uses_distance_pricing || false,
          monthlyFlatFeeAmount: Number(row.monthly_flat_fee_amount) || undefined,
          flatFeeStartDate: row.flat_fee_start_date || undefined,
          createdAt: row.created_at,
          updatedAt: row.updated_at
        }));

        mappedCustomers.sort((a, b) => a.name.localeCompare(b.name, 'sv'));

        setCustomers(mappedCustomers);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  }, []);

  const loadPurchases = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('time_purchases')
        .select('*')
        .order('purchase_date', { ascending: false });

      if (error) throw error;

      if (data) {
        const mappedPurchases: TimePurchase[] = data.map((row: any) => ({
          id: row.id,
          userId: row.user_id,
          customerId: row.customer_id,
          purchaseDate: row.purchase_date,
          hoursPurchased: Number(row.hours_purchased),
          hourlyRate: Number(row.hourly_rate),
          kmPurchased: Number(row.km_purchased) || 0,
          kmRate: Number(row.km_rate) || 0,
          totalAmount: Number(row.total_amount),
          description: row.description,
          invoiceNumber: row.invoice_number,
          createdAt: row.created_at,
          updatedAt: row.updated_at
        }));
        setPurchases(mappedPurchases);
      }
    } catch (error) {
      console.error('Error loading purchases:', error);
    }
  }, []);

  const loadTransactions = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('customer_transactions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data) {
        const mappedTransactions: CustomerTransaction[] = data.map((row: any) => ({
          id: row.id,
          userId: row.user_id,
          customerId: row.customer_id,
          transactionType: row.transaction_type,
          amountMinutes: Number(row.amount_minutes),
          balanceAfter: Number(row.balance_after),
          referenceId: row.reference_id,
          referenceType: row.reference_type,
          description: row.description,
          createdAt: row.created_at
        }));
        setTransactions(mappedTransactions);
      }
    } catch (error) {
      console.error('Error loading transactions:', error);
    }
  }, []);

  const loadAssignments = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('assignments')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (error.code === '42P01') {
          setHasMissingTablesError(true);
          return;
        }
        throw error;
      }

      if (data) {
        const mapped: Assignment[] = data.map((row: any) => ({
          id: row.id,
          timeBankId: row.time_bank_id || undefined,
          customerId: row.customer_id || null,
          name: row.name,
          description: row.description || '',
          assignmentNumber: row.assignment_number,
          caseNumber: row.case_number,
          createdDate: row.created_date || row.created_at || '',
          updatedAt: row.updated_at || undefined,
          status: row.status as 'active' | 'completed',
          requiresReturnTrip: row.requires_return_trip || false,
          spareParts: row.spare_parts || [],
          distanceZoneId: row.distance_zone_id || undefined,
          distanceFixedPrice: row.distance_fixed_price !== null ? Number(row.distance_fixed_price) : undefined,
          distanceFromPointKm: row.distance_from_point_km !== null ? Number(row.distance_from_point_km) : undefined,
          hardwareId: row.hardware_id || undefined,
          billingOverrideType: row.billing_override_type || undefined,
          overrideHourlyRate: row.override_hourly_rate !== null ? Number(row.override_hourly_rate) : undefined,
          overrideMinHours: row.override_min_hours !== null ? Number(row.override_min_hours) : undefined,
          overrideIncrementMinutes: row.override_increment_minutes !== null ? Number(row.override_increment_minutes) : undefined,
          billingOverrideReason: row.billing_override_reason || undefined,
          isOngoing: row.is_ongoing || false,
          billingTriggerType: row.billing_trigger_type || 'manual',
          billingTriggerValue: row.billing_trigger_value || null,
          displayMode: row.display_mode || 'both',
          lastInvoicedDate: row.last_invoiced_date || undefined
        }));
        setAssignments(mapped);
      }
    } catch (error) {
      console.error('Error loading assignments:', error);
    }
  }, []);

  const loadTimeEntries = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('time_entries')
        .select('*')
        .order('date', { ascending: false });

      if (error) {
        if (error.code === '42P01') {
          setHasMissingTablesError(true);
          return;
        }
        throw error;
      }

      if (data) {
        const mapped: TimeEntry[] = data.map((row: any) => {
          let spareParts = [];
          if (row.spare_parts) {
            try {
              spareParts = typeof row.spare_parts === 'string'
                ? JSON.parse(row.spare_parts)
                : row.spare_parts;
            } catch (e) {
              console.error('Error parsing spare parts:', e);
              spareParts = [];
            }
          }

          return {
            id: row.id,
            timeBankId: row.time_bank_id || undefined,
            customerId: row.customer_id || null,
            assignmentId: row.assignment_id || '',
            date: row.date,
            workTime: Number(row.work_time) || 0,
            travelTime: Number(row.travel_time) || 0,
            returnTravelTime: Number(row.return_travel_time) || 0,
            parkingCost: Number(row.parking_cost) || 0,
            kilometers: Number(row.kilometers) || 0,
            outbound_km: Number(row.outbound_km) || 0,
            return_km: Number(row.return_km) || 0,
            workStartTime: row.work_start_time || '',
            workEndTime: row.work_end_time || '',
            travelStartTime: row.travel_start_time || '',
            travelEndTime: row.travel_end_time || '',
            returnTravelStartTime: row.return_travel_start_time || '',
            returnTravelEndTime: row.return_travel_end_time || '',
            description: row.description || '',
            location: row.location || '',
            isReturnVisit: row.is_return_visit || false,
            isVirtualTravel: row.is_virtual_travel || false,
            isVirtualReturnTravel: row.is_virtual_return_travel || false,
            spareParts: spareParts,
            isInvoiced: row.is_invoiced || false,
            invoicedDate: row.invoiced_date || undefined,
            isBillable: row.is_billable !== undefined ? row.is_billable : true,
            nonBillableReason: row.non_billable_reason || undefined,
            isLocked: row.is_locked || false,
            lockedAt: row.locked_at || undefined,
            lockedByTimeBankId: row.locked_by_time_bank_id || undefined
          };
        });
        setTimeEntries(mapped);
      }
    } catch (error) {
      console.error('Error loading time entries:', error);
    }
  }, []);


  const loadData = useCallback(async () => {
    const now = Date.now();
    if (now - lastLoadTime.current < LOAD_DEBOUNCE_MS) {
      console.log('Data load skipped (debounced)');
      return;
    }
    lastLoadTime.current = now;

    setLoading(true);
    setHasMissingTablesError(false);
    try {
      console.log('Starting data load...');
      const startTime = Date.now();

      await Promise.all([
        loadCustomers().then(() => console.log(`Customers loaded in ${Date.now() - startTime}ms`)),
        loadPurchases().then(() => console.log(`Purchases loaded in ${Date.now() - startTime}ms`)),
        loadTransactions().then(() => console.log(`Transactions loaded in ${Date.now() - startTime}ms`)),
        loadAssignments().then(() => console.log(`Assignments loaded in ${Date.now() - startTime}ms`)),
        loadTimeEntries().then(() => console.log(`TimeEntries loaded in ${Date.now() - startTime}ms`))
      ]);

      console.log(`Total data load completed in ${Date.now() - startTime}ms`);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [loadCustomers, loadPurchases, loadTransactions, loadAssignments, loadTimeEntries]);

  useEffect(() => {
    if (!userId) return;
    setLoading(false);
    setTimeout(() => loadData(), 0);
  }, [userId, loadData]);

  useEffect(() => {
    if (!userId) return;

    let timeEntriesDebounce: NodeJS.Timeout | null = null;
    let purchasesDebounce: NodeJS.Timeout | null = null;
    let reconnectTimeout: NodeJS.Timeout | null = null;

    const setupChannels = () => {
      const timeEntriesChannel = supabase
        .channel('time_entries_changes', {
          config: {
            broadcast: { self: false }
          }
        })
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'time_entries' },
          () => {
            if (timeEntriesDebounce) clearTimeout(timeEntriesDebounce);
            timeEntriesDebounce = setTimeout(() => {
              loadTimeEntries();
              loadCustomers();
            }, 500);
          }
        )
        .subscribe((status) => {
          if (status === 'CHANNEL_ERROR') {
            console.error('Realtime channel error, attempting to reconnect...');
            if (reconnectTimeout) clearTimeout(reconnectTimeout);
            reconnectTimeout = setTimeout(() => {
              supabase.removeChannel(timeEntriesChannel);
              setupChannels();
            }, 5000);
          }
        });

      const assignmentsChannel = supabase
        .channel('assignments_changes', {
          config: {
            broadcast: { self: false }
          }
        })
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'assignments' },
          () => {
            loadAssignments();
          }
        )
        .subscribe();

      const customersChannel = supabase
        .channel('customers_changes', {
          config: {
            broadcast: { self: false }
          }
        })
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'customers' },
          () => {
            loadCustomers();
          }
        )
        .subscribe();

      const purchasesChannel = supabase
        .channel('purchases_changes', {
          config: {
            broadcast: { self: false }
          }
        })
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'time_purchases' },
          () => {
            if (purchasesDebounce) clearTimeout(purchasesDebounce);
            purchasesDebounce = setTimeout(() => {
              loadPurchases();
              loadCustomers();
            }, 500);
          }
        )
        .subscribe();

      return { timeEntriesChannel, assignmentsChannel, customersChannel, purchasesChannel };
    };

    const channels = setupChannels();

    const cleanupVisibility = setupVisibilityListener();
    const cleanupOnline = setupOnlineListener((online) => {
      setIsOnline(online);
      if (online) {
        loadData();
      }
    });

    return () => {
      if (timeEntriesDebounce) clearTimeout(timeEntriesDebounce);
      if (purchasesDebounce) clearTimeout(purchasesDebounce);
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      supabase.removeChannel(channels.timeEntriesChannel);
      supabase.removeChannel(channels.assignmentsChannel);
      supabase.removeChannel(channels.customersChannel);
      supabase.removeChannel(channels.purchasesChannel);
      if (cleanupVisibility) cleanupVisibility();
      if (cleanupOnline) cleanupOnline();
    };
  }, [userId, loadTimeEntries, loadAssignments, loadCustomers, loadPurchases, loadData]);

  const addCustomer = async (customer: Omit<Customer, 'id' | 'userId' | 'currentBalanceMinutes' | 'totalPurchasedMinutes' | 'totalUsedMinutes' | 'createdAt' | 'updatedAt'>) => {
    try {
      const insertData: any = {
        name: customer.name,
        email: customer.email,
        phone: customer.phone,
        organization_number: customer.organizationNumber,
        address: customer.address,
        notes: customer.notes,
        status: customer.status || 'active',
        parent_customer_id: customer.parentCustomerId || null,
        billing_type: customer.billingType || 'time_package',
        per_job_hourly_rate: customer.perJobHourlyRate || 595,
        per_job_min_billing_hours: customer.perJobMinBillingHours || 1.0,
        per_job_increment_minutes: customer.perJobIncrementMinutes || 30,
        per_job_km_rate: customer.perJobKmRate || 5.5,
        per_job_min_km_charge: customer.perJobMinKmCharge || 350,
        per_job_fixed_price: customer.perJobFixedPrice || null,
        km_rate: customer.kmRate || 5.5,
        current_balance_minutes: 0,
        total_purchased_minutes: 0,
        total_used_minutes: 0
      };

      if (userId) {
        insertData.user_id = userId;
      }

      const { data, error } = await supabase
        .from('customers')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      await loadCustomers();
      return data;
    } catch (error) {
      console.error('Error adding customer:', error);
      alert('Kunde inte lägga till kund: ' + (error as Error).message);
    }
  };

  const updateCustomer = async (id: string, updates: Partial<Customer>) => {
    try {
      const updateData: any = {};

      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.email !== undefined) updateData.email = updates.email;
      if (updates.phone !== undefined) updateData.phone = updates.phone;
      if (updates.organizationNumber !== undefined) updateData.organization_number = updates.organizationNumber;
      if (updates.address !== undefined) updateData.address = updates.address;
      if (updates.notes !== undefined) updateData.notes = updates.notes;
      if (updates.status !== undefined) updateData.status = updates.status;
      if (updates.userId !== undefined) updateData.user_id = updates.userId;
      if (updates.parentCustomerId !== undefined) updateData.parent_customer_id = updates.parentCustomerId;
      if (updates.billingType !== undefined) updateData.billing_type = updates.billingType;
      if (updates.perJobHourlyRate !== undefined) updateData.per_job_hourly_rate = updates.perJobHourlyRate;
      if (updates.perJobMinBillingHours !== undefined) updateData.per_job_min_billing_hours = updates.perJobMinBillingHours;
      if (updates.perJobIncrementMinutes !== undefined) updateData.per_job_increment_minutes = updates.perJobIncrementMinutes;
      if (updates.perJobKmRate !== undefined) updateData.per_job_km_rate = updates.perJobKmRate;
      if (updates.perJobMinKmCharge !== undefined) updateData.per_job_min_km_charge = updates.perJobMinKmCharge;
      if (updates.perJobFixedPrice !== undefined) updateData.per_job_fixed_price = updates.perJobFixedPrice;
      if (updates.kmRate !== undefined) updateData.km_rate = updates.kmRate;
      if (updates.usesDistancePricing !== undefined) updateData.uses_distance_pricing = updates.usesDistancePricing;
      if (updates.monthlyFlatFeeAmount !== undefined) updateData.monthly_flat_fee_amount = updates.monthlyFlatFeeAmount;
      if (updates.flatFeeStartDate !== undefined) updateData.flat_fee_start_date = updates.flatFeeStartDate;

      const { error } = await supabase
        .from('customers')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      await loadCustomers();
    } catch (error) {
      console.error('Error updating customer:', error);
      alert('Kunde inte uppdatera kund: ' + (error as Error).message);
    }
  };

  const deleteCustomer = async (id: string) => {
    try {
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await loadCustomers();
    } catch (error) {
      console.error('Error deleting customer:', error);
      alert('Kunde inte radera kund: ' + (error as Error).message);
    }
  };

  const addPurchase = async (purchase: Omit<TimePurchase, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => {
    try {
      const insertData: any = {
        customer_id: purchase.customerId,
        purchase_date: purchase.purchaseDate,
        hours_purchased: purchase.hoursPurchased,
        hourly_rate: purchase.hourlyRate,
        km_purchased: purchase.kmPurchased || 0,
        km_rate: purchase.kmRate || 0,
        km_invoiced: purchase.kmInvoiced || 0,
        km_invoice_amount: purchase.kmInvoiceAmount || 0,
        parking_invoiced: purchase.parkingInvoiced || 0,
        total_amount: purchase.totalAmount,
        description: purchase.description,
        invoice_number: purchase.invoiceNumber
      };

      if (userId) {
        insertData.user_id = userId;
      }

      const { data, error } = await supabase
        .from('time_purchases')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      if (purchase.kmInvoiced && purchase.kmInvoiced > 0) {
        const { error: kmError } = await supabase
          .from('time_entries')
          .update({
            km_invoiced: true,
            km_invoiced_date: purchase.purchaseDate
          })
          .eq('customer_id', purchase.customerId)
          .eq('km_invoiced', false)
          .or('outbound_km.gt.0,return_km.gt.0');

        if (kmError) {
          console.error('Error marking km as invoiced:', kmError);
        }
      }

      if (purchase.parkingInvoiced && purchase.parkingInvoiced > 0) {
        const { error: parkingError } = await supabase
          .from('time_entries')
          .update({
            parking_invoiced: true,
            parking_invoiced_date: purchase.purchaseDate
          })
          .eq('customer_id', purchase.customerId)
          .eq('parking_invoiced', false)
          .gt('parking_cost', 0);

        if (parkingError) {
          console.error('Error marking parking as invoiced:', parkingError);
        }
      }

      await loadPurchases();
      await loadCustomers();
      await loadTransactions();

      return data;
    } catch (error) {
      console.error('Error adding purchase:', error);
      alert('Kunde inte registrera köp: ' + (error as Error).message);
    }
  };

  const addAssignment = async (assignment: Omit<Assignment, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      // Validate session before attempting insert
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        console.error('Session validation failed:', sessionError);
        const error = new Error('Din session har gått ut. Vänligen ladda om sidan och logga in igen.');
        throw error;
      }

      const insertData: any = {
        customer_id: assignment.customerId,
        name: assignment.name,
        description: assignment.description,
        case_number: assignment.caseNumber,
        status: assignment.status,
        created_date: assignment.createdDate
      };

      if (assignment.distanceZoneId) {
        insertData.distance_zone_id = assignment.distanceZoneId;
      }
      if (assignment.distanceFixedPrice !== undefined) {
        insertData.distance_fixed_price = assignment.distanceFixedPrice;
      }
      if (assignment.distanceFromPointKm !== undefined) {
        insertData.distance_from_point_km = assignment.distanceFromPointKm;
      }
      if (assignment.billingOverrideType) {
        insertData.billing_override_type = assignment.billingOverrideType;
      }
      if (assignment.billingOverrideReason) {
        insertData.billing_override_reason = assignment.billingOverrideReason;
      }
      if (assignment.overrideHourlyRate !== undefined) {
        insertData.override_hourly_rate = assignment.overrideHourlyRate;
      }
      if (assignment.overrideMinHours !== undefined) {
        insertData.override_min_hours = assignment.overrideMinHours;
      }
      if (assignment.overrideIncrementMinutes !== undefined) {
        insertData.override_increment_minutes = assignment.overrideIncrementMinutes;
      }
      if (assignment.hardwareId) {
        insertData.hardware_id = assignment.hardwareId;
      }

      const { data, error } = await supabase
        .from('assignments')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        console.error('Database insert error:', error);

        // Check if it's an auth error
        if (error.code === 'PGRST301' || error.message?.includes('JWT') || error.message?.includes('session')) {
          const authError = new Error('Din session har gått ut. Vänligen ladda om sidan och logga in igen.');
          throw authError;
        }

        throw error;
      }

      await loadAssignments();

      return {
        id: data.id,
        customerId: data.customer_id,
        name: data.name,
        description: data.description || '',
        assignmentNumber: data.assignment_number,
        caseNumber: data.case_number,
        createdDate: data.created_date || data.created_at,
        status: data.status as 'active' | 'completed',
        distanceZoneId: data.distance_zone_id,
        distanceFixedPrice: data.distance_fixed_price,
        distanceFromPointKm: data.distance_from_point_km,
        billingOverrideType: data.billing_override_type,
        billingOverrideReason: data.billing_override_reason,
        overrideHourlyRate: data.override_hourly_rate,
        overrideMinHours: data.override_min_hours,
        overrideIncrementMinutes: data.override_increment_minutes,
        hardwareId: data.hardware_id
      };
    } catch (error) {
      console.error('Error adding assignment:', error);
      throw error;
    }
  };

  const updateAssignment = async (id: string, updates: any) => {
    try {
      // Validate session before attempting update
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        console.error('Session validation failed:', sessionError);
        const error = new Error('Din session har gått ut. Vänligen ladda om sidan och logga in igen.');
        throw error;
      }

      const dbUpdates: any = { ...updates };

      if (updates.spareParts !== undefined) {
        dbUpdates.spare_parts = updates.spareParts;
        delete dbUpdates.spareParts;
      }
      if (updates.hardwareId !== undefined) {
        dbUpdates.hardware_id = updates.hardwareId;
        delete dbUpdates.hardwareId;
      }

      console.log('Updating assignment in database:', id, dbUpdates);

      const { error } = await supabase
        .from('assignments')
        .update(dbUpdates)
        .eq('id', id);

      if (error) {
        console.error('Database update error:', error);

        // Check if it's an auth error
        if (error.code === 'PGRST301' || error.message?.includes('JWT') || error.message?.includes('session')) {
          const authError = new Error('Din session har gått ut. Vänligen ladda om sidan och logga in igen.');
          throw authError;
        }

        throw error;
      }

      console.log('Assignment updated successfully');

      if (updates.customer_id !== undefined) {
        const { error: timeEntriesError } = await supabase
          .from('time_entries')
          .update({ customer_id: updates.customer_id })
          .eq('assignment_id', id);

        if (timeEntriesError) throw timeEntriesError;
      }

      await loadAssignments();
      await loadTimeEntries();
      await loadCustomers();
      await loadTransactions();
    } catch (error) {
      console.error('Error updating assignment:', error);
      throw error;
    }
  };

  const deleteAssignment = async (id: string) => {
    try {
      const { error: entriesError } = await supabase
        .from('time_entries')
        .delete()
        .eq('assignment_id', id);

      if (entriesError) throw entriesError;

      const { error: assignmentError } = await supabase
        .from('assignments')
        .delete()
        .eq('id', id);

      if (assignmentError) throw assignmentError;

      await loadAssignments();
      await loadTimeEntries();
    } catch (error) {
      console.error('Error deleting assignment:', error);
      throw error;
    }
  };

  const addTimeEntry = async (timeEntry: Omit<TimeEntry, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      if (!navigator.onLine) {
        throw new Error('Ingen internetanslutning. Kontrollera din nätverksanslutning och försök igen.');
      }

      const customer = customers.find(c => c.id === timeEntry.customerId);
      const isMonthlyFlatFee = customer?.billingType === 'monthly_flat_fee';

      const insertData: any = {
        customer_id: timeEntry.customerId,
        assignment_id: timeEntry.assignmentId,
        date: timeEntry.date,
        work_time: Math.round(timeEntry.workTime),
        travel_time: Math.round(timeEntry.travelTime),
        return_travel_time: Math.round(timeEntry.returnTravelTime || 0),
        parking_cost: timeEntry.parkingCost || 0,
        kilometers: timeEntry.kilometers || 0,
        outbound_km: timeEntry.outbound_km || 0,
        return_km: timeEntry.return_km || 0,
        work_start_time: timeEntry.workStartTime || null,
        work_end_time: timeEntry.workEndTime || null,
        travel_start_time: timeEntry.travelStartTime || null,
        travel_end_time: timeEntry.travelEndTime || null,
        return_travel_start_time: timeEntry.returnTravelStartTime || null,
        return_travel_end_time: timeEntry.returnTravelEndTime || null,
        description: timeEntry.description || '',
        location: timeEntry.location || '',
        is_return_visit: timeEntry.isReturnVisit || false,
        is_virtual_travel: timeEntry.isVirtualTravel || false,
        is_virtual_return_travel: timeEntry.isVirtualReturnTravel || false,
        spare_parts: timeEntry.spareParts && timeEntry.spareParts.length > 0
          ? timeEntry.spareParts
          : [],
        is_billable: timeEntry.isBillable !== undefined ? timeEntry.isBillable : true,
        non_billable_reason: timeEntry.nonBillableReason || null,
        is_invoiced: isMonthlyFlatFee ? true : false,
        invoiced_date: isMonthlyFlatFee ? new Date().toISOString() : null,
        km_invoiced: isMonthlyFlatFee ? true : false,
        km_invoiced_date: isMonthlyFlatFee ? new Date().toISOString() : null,
        parking_invoiced: isMonthlyFlatFee ? true : false,
        parking_invoiced_date: isMonthlyFlatFee ? new Date().toISOString() : null
      };

      await retryWithBackoff(async () => {
        const { error } = await supabase
          .from('time_entries')
          .insert([insertData])
          .select()
          .single();

        if (error) throw error;
      }, { maxRetries: 3, delayMs: 1000, backoff: true });

      await loadTimeEntries();
      await loadCustomers();
      await loadTransactions();
    } catch (error) {
      console.error('Error adding time entry:', error);
      throw error;
    }
  };

  const updateTimeEntry = async (id: string, updates: Partial<TimeEntry>) => {
    try {
      const dbUpdates: any = {};

      if (updates.customerId !== undefined) dbUpdates.customer_id = updates.customerId;
      if (updates.assignmentId !== undefined) dbUpdates.assignment_id = updates.assignmentId;
      if (updates.timeBankId !== undefined) dbUpdates.time_bank_id = updates.timeBankId;
      if (updates.date !== undefined) dbUpdates.date = updates.date;
      if (updates.workTime !== undefined) dbUpdates.work_time = updates.workTime;
      if (updates.travelTime !== undefined) dbUpdates.travel_time = updates.travelTime;
      if (updates.returnTravelTime !== undefined) dbUpdates.return_travel_time = updates.returnTravelTime;
      if (updates.parkingCost !== undefined) dbUpdates.parking_cost = updates.parkingCost;
      if (updates.kilometers !== undefined) dbUpdates.kilometers = updates.kilometers;
      if (updates.outbound_km !== undefined) dbUpdates.outbound_km = updates.outbound_km;
      if (updates.return_km !== undefined) dbUpdates.return_km = updates.return_km;
      if (updates.workStartTime !== undefined) dbUpdates.work_start_time = updates.workStartTime;
      if (updates.workEndTime !== undefined) dbUpdates.work_end_time = updates.workEndTime;
      if (updates.travelStartTime !== undefined) dbUpdates.travel_start_time = updates.travelStartTime;
      if (updates.travelEndTime !== undefined) dbUpdates.travel_end_time = updates.travelEndTime;
      if (updates.returnTravelStartTime !== undefined) dbUpdates.return_travel_start_time = updates.returnTravelStartTime;
      if (updates.returnTravelEndTime !== undefined) dbUpdates.return_travel_end_time = updates.returnTravelEndTime;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.location !== undefined) dbUpdates.location = updates.location;
      if (updates.isReturnVisit !== undefined) dbUpdates.is_return_visit = updates.isReturnVisit;
      if (updates.isVirtualTravel !== undefined) dbUpdates.is_virtual_travel = updates.isVirtualTravel;
      if (updates.isVirtualReturnTravel !== undefined) dbUpdates.is_virtual_return_travel = updates.isVirtualReturnTravel;
      if (updates.isBillable !== undefined) dbUpdates.is_billable = updates.isBillable;
      if (updates.nonBillableReason !== undefined) dbUpdates.non_billable_reason = updates.nonBillableReason;
      if (updates.spareParts !== undefined) {
        dbUpdates.spare_parts = Array.isArray(updates.spareParts) && updates.spareParts.length > 0
          ? updates.spareParts
          : [];
      }

      const { error } = await supabase
        .from('time_entries')
        .update(dbUpdates)
        .eq('id', id);

      if (error) throw error;

      await loadTimeEntries();
      await loadCustomers();
      await loadTransactions();
    } catch (error) {
      console.error('Error updating time entry:', error);
      throw error;
    }
  };

  const deleteTimeEntry = async (id: string) => {
    try {
      const { error } = await supabase
        .from('time_entries')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await loadTimeEntries();
      await loadCustomers();
      await loadTransactions();
    } catch (error) {
      console.error('Error deleting time entry:', error);
      throw error;
    }
  };

  return {
    customers,
    purchases,
    transactions,
    assignments,
    timeEntries,
    loading,
    hasMissingTablesError,
    isOnline,
    loadData,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    addPurchase,
    addAssignment,
    updateAssignment,
    deleteAssignment,
    addTimeEntry,
    updateTimeEntry,
    deleteTimeEntry
  };
};
